import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSelector, useDispatch } from 'react-redux';
import { AlertCircle, AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';
import { RootState } from '@/redux/root-reducers';
import type { AppDispatch } from '@/redux/store';
import { clearMessageAction, setMessageAction } from '@/redux/slicers/api-message.slice';
import { cn } from '@/utils/classnames';
import { Button } from '@/components/ui/button';

interface ToastProps {
  id: string;
  message: MessageOptions;
  onClose: () => void;
}

const toastMetaMap = {
  success: {
    icon: CheckCircle2,
    className: 'border-[#49D3A4] bg-[#E7F8F2] text-[#008B63] dark:border-[#0FA875] dark:bg-[#062C24] dark:text-[#53D39B]',
  },
  error: {
    icon: XCircle,
    className: 'border-[#FF7D83] bg-[#FDECEC] text-[#EF4444] dark:border-[#D93E3E] dark:bg-[#331618] dark:text-[#FF8A8F]',
  },
  warning: {
    icon: AlertTriangle,
    className: 'border-[#FFBC58] bg-[#FEF6E9] text-[#B87509] dark:border-[#AC741C] dark:bg-[#302313] dark:text-[#F2B95A]',
  },
  info: {
    icon: AlertCircle,
    className: 'border-[#7FA6FF] bg-[#E9EFFD] text-[#2F61C9] dark:border-[#3A89FE] dark:bg-[#10264D] dark:text-[#7EA2FF]',
  },
} satisfies Record<MessageAPIType, { icon: React.ElementType; className: string }>;

const Toast: React.FC<ToastProps> = ({ message, onClose }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(false);
    const animationFrame = requestAnimationFrame(() => setIsVisible(true));

    // Auto hide after 5 seconds
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(onClose, 300); // Wait for animation to complete
    }, 5000);

    return () => {
      cancelAnimationFrame(animationFrame);
      clearTimeout(timer);
    };
  }, [message.messages, message.type, onClose]);

  const meta = toastMetaMap[message.type] ?? {
    icon: Info,
    className: 'border-[#A8AFBA] bg-[#D1D3D8] text-[#3A3F49] dark:border-[#566174] dark:bg-[#202A3A] dark:text-[#D9E2F2]',
  };
  const Icon = meta.icon;

  return (
    <div
      className={cn(
        'pointer-events-auto w-fit transform rounded-[1rem] border px-[1.6rem] py-[1.2rem] shadow-[0px_8px_24px_0px_#001C5914] transition-all duration-300 ease-in-out dark:shadow-[0px_12px_28px_0px_rgba(0,0,0,0.38)]',
        isVisible ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0',
        meta.className,
      )}
    >
      <div className="flex items-center gap-[1rem]">
        <Icon className="h-[2rem] w-[2rem] shrink-0" strokeWidth={1.9} />
        <div className="min-w-0 flex-1">
          {message.messages.map((msg, index) => (
            <div
              key={`${msg}-${index}`}
              className={cn(
                'break-words',
                index === 0
                  ? 'text-[1.4rem] font-medium leading-[2rem]'
                  : 'mt-[0.2rem] text-[1.2rem] font-normal leading-[1.7rem] opacity-80',
              )}
            >
              {msg}
            </div>
          ))}
        </div>
        <Button
          type="button"
          variant="ghost"
          aria-label="Close notification"
          onClick={onClose}
          className="inline-flex h-[2.4rem] w-[2.4rem] shrink-0 items-center justify-center rounded-full opacity-70 transition-opacity hover:bg-black/5 hover:opacity-100 dark:hover:bg-white/10"
        >
          <X className="h-[1.6rem] w-[1.6rem]" strokeWidth={2} />
        </Button>
      </div>
    </div>
  );
};

export const ToastContainer: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const messages = useSelector((state: RootState) => state.apiMessage.box);

  // Set dispatch for toast function
  useEffect(() => {
    setToastDispatch(dispatch);
  }, [dispatch]);

  const handleClose = (id: string) => {
    dispatch(clearMessageAction([id]));
  };

  const content = (
    <div className="pointer-events-none fixed right-[1.6rem] top-[1.6rem] z-[2147483647] flex w-[calc(100vw-3.2rem)] max-w-[36rem] flex-col items-end gap-[0.8rem]">
      {Object.entries(messages).map(([id, message]) => {
        if (!message) return null;
        return <Toast key={id} id={id} message={message} onClose={() => handleClose(id)} />;
      })}
    </div>
  );

  return typeof document === 'undefined'
    ? content
    : createPortal(content, document.body);
};

// Toast function để sử dụng trong hooks
let toastDispatch: AppDispatch | null = null;

export const setToastDispatch = (dispatch: AppDispatch) => {
  toastDispatch = dispatch;
};

export const toast = (options: {
  title: string;
  description: string;
  type: 'success' | 'error' | 'warning' | 'info';
}) => {
  const dispatch = toastDispatch;
  if (!dispatch) {
    console.warn('Toast dispatch not set. Toast will not be displayed.');
    return;
  }

  const id = Date.now().toString();

  const message: MessageOptions = {
    type: options.type,
    messages: [options.title, options.description].filter(Boolean),
  };

  dispatch(setMessageAction([id, message]));

  // Auto remove after 5 seconds
  setTimeout(() => {
    dispatch(clearMessageAction([id]));
  }, 5000);
};
